/**
 * Kickoff intro — the scripted pre-match camera presentation. (D0 broadcast
 * feel; addendum "start the game as how an official game starts")
 *
 * A ~4s sideline flyby over the frozen kickoff tableau: the camera starts high
 * behind a corner flag, sweeps along the touchline while a team lower-third
 * shows the fixture, and BLENDS into wherever the live camera rig wants to be
 * (the last quarter of the flight interpolates toward the real CameraPose the
 * caller computes each frame, so the handover to BROADCAST is seamless by
 * construction — no snap, no matter what the rig's settings are).
 *
 * The caller owns the loop: it keeps calling `camera.update()` as normal (which
 * also warms the rig's smoothing toward its own target) and, while `active`,
 * applies the pose this module writes instead. game/practice.ts freezes the sim
 * during the intro — a kickoff tableau is exactly the broadcast look — and
 * resets the step driver on handover so no "missed" time is ever simulated.
 *
 * ESC / click / Space / Enter SKIPS instantly (capture-phase listeners, so the
 * skip press never leaks into InputState as a game action).
 */

import type { CameraPose } from '../render/camera.js';

export interface MatchIntroOptions {
  nameA: string;
  nameB: string;
  pitchLength: number;
  pitchWidth: number;
  /**
   * Polled once the flight timer reaches 1 to decide whether it's safe to
   * cut to gameplay. Defaults to always-ready. `false` holds the intro at
   * its fully-blended final pose — already the live camera rig's own pose
   * by then (see BLEND_TAIL), so this reads as the broadcast shot lingering
   * on the tableau a moment longer, not a freeze — instead of finishing and
   * revealing whatever the caller isn't ready to show yet (e.g. a cast
   * still mid-load: castPlayerVisual.ts draws nothing until every character
   * model is in, so cutting away too early is an empty pitch, then players
   * popping in one by one). Does not affect `skip()` — an impatient player
   * can always bail out immediately, readiness or not.
   */
  isReady?(): boolean;
  /** Fired exactly once, when the intro ends (naturally or skipped). */
  onDone(): void;
}

export interface MatchIntro {
  readonly active: boolean;
  /**
   * Advance the intro and write the camera pose for this frame into `out`.
   * `livePose` is what the normal camera rig computed this frame — the intro
   * converges onto it. No-op once inactive.
   */
  update(dtS: number, livePose: CameraPose, out: CameraPose): void;
  skip(): void;
  dispose(): void;
}

const INTRO_S = 4.2;
/** Last fraction of the flight spent converging onto the live camera pose. */
const BLEND_TAIL = 0.28;
const STING_MS = 1600;

function smoothstep01(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}
function mix(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

export function mountMatchIntro(container: HTMLElement, opts: MatchIntroOptions): MatchIntro {
  const root = document.createElement('div');
  root.className = 'fgintro';
  root.innerHTML = `
    <div class="fgintro-third">
      <span class="fgintro-team">${opts.nameA}</span>
      <span class="fgintro-vs">VS</span>
      <span class="fgintro-team">${opts.nameB}</span>
    </div>
    <p class="fgintro-skip">ESC / CLICK TO SKIP</p>`;
  container.appendChild(root);
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  // Flight path: high behind the -X corner → the +Z touchline broadcast side.
  const L = opts.pitchLength * 0.5;
  const W = opts.pitchWidth * 0.5;
  const startX = -L * 0.9;
  const startY = 16;
  const startZ = W * 1.35;
  const endX = 0;
  const endY = 19; // near the stock broadcast rig; the tail blend closes the gap
  const endZ = W + 26;

  let t = 0;
  let active = true;
  let doneFired = false;

  const finish = (): void => {
    if (doneFired) return;
    doneFired = true;
    active = false;
    root.remove();
    // "KICK OFF" sting — brief, then gone. Its own element so intro teardown
    // stays one-shot. Also the honest pointer-lock hint: the browser only
    // grants the lock on a user gesture, so the player clicks to take control.
    const sting = document.createElement('div');
    sting.className = 'fgintro-sting';
    sting.innerHTML = `<span class="fgintro-stingword">KICK OFF</span><span class="fgintro-stinghint">CLICK TO TAKE CONTROL</span>`;
    container.appendChild(sting);
    window.setTimeout(() => {
      sting.remove();
      style.remove();
    }, STING_MS);
    detach();
    opts.onDone();
  };

  // Capture-phase so the skip press never reaches the game's own listeners.
  const onKey = (e: KeyboardEvent): void => {
    if (!active) return;
    if (e.code === 'Escape' || e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault();
      e.stopImmediatePropagation();
      finish();
    }
  };
  const onClick = (e: MouseEvent): void => {
    if (!active) return;
    e.stopImmediatePropagation();
    finish();
  };
  window.addEventListener('keydown', onKey, { capture: true });
  window.addEventListener('mousedown', onClick, { capture: true });
  const detach = (): void => {
    window.removeEventListener('keydown', onKey, { capture: true });
    window.removeEventListener('mousedown', onClick, { capture: true });
  };

  return {
    get active(): boolean {
      return active;
    },
    update(dtS: number, livePose: CameraPose, out: CameraPose): void {
      if (!active) return;
      t += dtS;
      const p = Math.min(1, t / INTRO_S);
      const e = smoothstep01(p);

      // Spline leg: eased sweep, always looking at the centre circle.
      const sx = mix(startX, endX, e);
      const sy = mix(startY, endY, e);
      const sz = mix(startZ, endZ, e);
      const lookX = 0;
      const lookY = 0.4;
      const lookZ = 0;
      const fov = mix(60, 54, e);

      // Tail: converge onto whatever the live rig wants (seamless handover).
      const k = smoothstep01((p - (1 - BLEND_TAIL)) / BLEND_TAIL);
      out.x = mix(sx, livePose.x, k);
      out.y = mix(sy, livePose.y, k);
      out.z = mix(sz, livePose.z, k);
      out.lookX = mix(lookX, livePose.lookX, k);
      out.lookY = mix(lookY, livePose.lookY, k);
      out.lookZ = mix(lookZ, livePose.lookZ, k);
      out.fov = mix(fov, livePose.fov, k);
      out.showBallArrow = false;
      out.ballArrowYaw = 0;
      out.offBallBlend = 0;

      if (p >= 1 && (opts.isReady?.() ?? true)) finish();
    },
    skip(): void {
      finish();
    },
    dispose(): void {
      active = false;
      doneFired = true; // never fire onDone from a disposed intro
      detach();
      root.remove();
      style.remove();
    },
  };
}

const CSS = `
.fgintro { position: fixed; inset: 0; z-index: 22; pointer-events: none;
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, sans-serif; color: #fff; user-select: none; }
.fgintro-third {
  position: absolute; left: 50%; bottom: 12vh; transform: translateX(-50%) skewX(-10deg);
  display: flex; align-items: center; gap: 18px;
  padding: 12px 34px;
  background: linear-gradient(180deg, rgba(32,37,50,0.97) 0%, rgba(14,17,25,0.95) 100%);
  border-left: 4px solid #ffd94a; border-radius: 3px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: fgintroThird 700ms cubic-bezier(0.2, 0.9, 0.3, 1) 350ms both;
}
.fgintro-team { font-size: 22px; font-weight: 900; letter-spacing: 3px; font-style: italic; transform: skewX(10deg); }
.fgintro-vs { font-size: 12px; font-weight: 900; color: #ffd94a; letter-spacing: 2px; transform: skewX(10deg); }
@keyframes fgintroThird { from { opacity: 0; transform: translateX(-50%) skewX(-10deg) translateY(30px); } }
.fgintro-skip {
  position: absolute; right: 22px; bottom: 18px; margin: 0;
  font-size: 10px; letter-spacing: 2.5px; color: rgba(255,255,255,0.45);
  animation: fgintroPulse 1.8s ease-in-out infinite;
}
@keyframes fgintroPulse { 50% { opacity: 0.4; } }
.fgintro-sting {
  position: fixed; left: 50%; top: 38vh; transform: translateX(-50%); z-index: 22;
  display: flex; flex-direction: column; align-items: center; gap: 8px; pointer-events: none;
  font-family: 'Segoe UI', system-ui, sans-serif; user-select: none;
}
.fgintro-stingword {
  font-size: 44px; font-weight: 900; font-style: italic; letter-spacing: 8px; color: #ffd94a;
  text-shadow: 0 4px 0 rgba(0,0,0,0.4), 0 10px 34px rgba(0,0,0,0.6);
  animation: fgintroSting 420ms cubic-bezier(0.2, 1.5, 0.3, 1) both, fgintroFade 400ms ease-in ${STING_MS - 400}ms forwards;
}
.fgintro-stinghint {
  font-size: 11px; letter-spacing: 3px; color: rgba(255,255,255,0.75);
  animation: fgintroFade 400ms ease-in ${STING_MS - 400}ms forwards;
}
@keyframes fgintroSting { from { opacity: 0; transform: scale(0.5); } }
@keyframes fgintroFade { to { opacity: 0; } }
`;
